/**
 * Optimistic Updates Utility
 * Provides timeout-based rollback and better error recovery for optimistic updates
 */

// Configuration constants
const OPTIMISTIC_UPDATE_CONFIG = {
    TIMEOUT_MS: 30000, // 30 seconds timeout
    MAX_RETRIES: 2,
    RETRY_DELAY_MS: 1000, // 1 second between retries
};

/**
 * Creates a timeout promise that rejects after the specified time
 */
const createTimeout = (ms) => {
    return new Promise((_, reject) => {
        setTimeout(() => {
            reject(new Error(`Operation timed out after ${ms}ms`));
        }, ms);
    });
};

/**
 * Executes an async operation with timeout protection
 * @param {Promise} operation - The async operation to execute
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {Promise} The operation result or timeout error
 */
export const withTimeout = async (operation, timeoutMs = OPTIMISTIC_UPDATE_CONFIG.TIMEOUT_MS) => {
    return Promise.race([
        operation,
        createTimeout(timeoutMs),
    ]);
};

/**
 * Retries an async operation with exponential backoff
 * @param {Function} operation - The async operation function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} delayMs - Initial delay between retries in milliseconds
 * @returns {Promise} The operation result
 */
export const withRetry = async (operation, maxRetries = OPTIMISTIC_UPDATE_CONFIG.MAX_RETRIES, delayMs = OPTIMISTIC_UPDATE_CONFIG.RETRY_DELAY_MS) => {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error;
            
            // Don't retry on certain error types
            if (error.response?.status === 400 || error.response?.status === 404) {
                throw error; // Bad request or not found - don't retry
            }
            
            // If this is the last attempt, throw the error
            if (attempt === maxRetries) {
                throw error;
            }
            
            // Wait before retrying (exponential backoff)
            const waitTime = delayMs * Math.pow(2, attempt);
            await new Promise(resolve => setTimeout(resolve, waitTime));
        }
    }
    
    throw lastError;
};

/**
 * Determines if an error is retryable
 * @param {Error} error - The error to check
 * @returns {boolean} True if the error is retryable
 */
export const isRetryableError = (error) => {
    // Network errors are retryable
    if (!error.response) {
        return true;
    }
    
    const status = error.response?.status;
    
    // 5xx errors are retryable
    if (status >= 500 && status < 600) {
        return true;
    }
    
    // 408 Request Timeout is retryable
    if (status === 408) {
        return true;
    }
    
    // 429 Too Many Requests is retryable
    if (status === 429) {
        return true;
    }
    
    // 4xx client errors are generally not retryable
    return false;
};

/**
 * Gets a user-friendly error message based on error type
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
    if (!error) {
        return 'An unexpected error occurred';
    }
    
    // Timeout error
    if (error.message?.includes('timed out')) {
        return 'The operation took too long. Please try again.';
    }
    
    // Network error
    if (!error.response) {
        return 'Network error. Please check your connection and try again.';
    }
    
    const status = error.response?.status;
    const message = error.response?.data?.message || error.message;
    
    // Specific error messages
    if (status === 400) {
        return message || 'Invalid request. Please check your input.';
    }
    
    if (status === 401) {
        return 'Your session has expired. Please refresh the page.';
    }
    
    if (status === 403) {
        return 'You do not have permission to perform this action.';
    }
    
    if (status === 404) {
        return 'The event was not found. It may have been deleted.';
    }
    
    if (status === 408) {
        return 'Request timed out. Please try again.';
    }
    
    if (status === 429) {
        return 'Too many requests. Please wait a moment and try again.';
    }
    
    if (status >= 500) {
        return 'Server error. Please try again later.';
    }
    
    return message || 'An error occurred while updating the event.';
};

/**
 * Creates a rollback function for optimistic updates
 * @param {Function} setOptimisticEdits - State setter for optimistic edits
 * @param {string} key - The event key to rollback
 * @returns {Function} Rollback function
 */
export const createRollback = (setOptimisticEdits, key) => {
    return () => {
        setOptimisticEdits((prev) => {
            const { [key]: _, ...rest } = prev;
            return rest;
        });
    };
};

/**
 * Executes an optimistic update with timeout and error recovery
 * @param {Object} options - Configuration options
 * @param {Function} options.setOptimisticEdits - State setter for optimistic edits
 * @param {string} options.key - The event key
 * @param {Object} options.optimisticData - The optimistic update data
 * @param {Function} options.operation - The async operation to execute
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @param {number} options.timeoutMs - Timeout in milliseconds
 * @param {boolean} options.enableRetry - Whether to enable retry on failure
 * @returns {Promise} The operation result
 */
export const executeOptimisticUpdate = async ({
    setOptimisticEdits,
    key,
    optimisticData,
    operation,
    onSuccess,
    onError,
    timeoutMs = OPTIMISTIC_UPDATE_CONFIG.TIMEOUT_MS,
    enableRetry = true,
}) => {
    const rollback = createRollback(setOptimisticEdits, key);
    let timeoutId = null;
    
    // Set up timeout-based rollback
    const setupTimeoutRollback = () => {
        timeoutId = setTimeout(() => {
            rollback();
            const error = new Error(`Operation timed out after ${timeoutMs}ms`);
            if (onError) {
                onError(error, true); // true indicates timeout
            }
        }, timeoutMs);
    };
    
    // Apply optimistic update
    setOptimisticEdits((prev) => ({
        ...prev,
        [key]: optimisticData,
    }));
    
    // Set up timeout rollback
    setupTimeoutRollback();
    
    try {
        // Execute operation with timeout and optional retry
        const executeOperation = () => withTimeout(operation(), timeoutMs);
        
        let result;
        if (enableRetry) {
            // Use retry logic - withRetry will check if error is retryable internally
            result = await withRetry(async () => {
                return await executeOperation();
            });
        } else {
            result = await executeOperation();
        }
        
        // Clear timeout on success
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        
        // Remove optimistic update
        rollback();
        
        // Call success callback
        if (onSuccess) {
            onSuccess(result);
        }
        
        return result;
    } catch (error) {
        // Clear timeout on error
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        
        // Rollback optimistic update
        rollback();
        
        // Call error callback
        if (onError) {
            const isTimeout = error.message?.includes('timed out') || error.message?.includes('timeout');
            onError(error, isTimeout);
        }
        
        throw error;
    }
};

